import {
  CLASS_CODE,
  type ClassDetail,
  type ClassSummary,
  type JoinPreview,
} from '@pebblecode/shared';
import { useEffect, useState, type FormEvent } from 'react';

import { api, ApiError } from '../api/client.js';
import { navigate, onLinkClick, useTitle } from '../app/router.js';
import { Icon } from '../components/Icon.js';
import { FormError, LoginRequired, PageShell } from '../components/PageShell.js';
import { useAction } from '../components/useAction.js';
import { ClassHomework } from '../homework/ClassHomework.js';
import { useT } from '../i18n/index.js';
import { isTeacher, useMe } from '../state/auth.js';
import { AccountBanners } from './AccountBanners.js';

/** Type a class code; used on /classes and on the home page. */
export function JoinClassForm() {
  const t = useT();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!CLASS_CODE.test(c)) {
      setError(t.classes.joinInvalid);
      return;
    }
    navigate(`/join/${c}`);
  };
  return (
    <form className="inline-form" onSubmit={submit}>
      <input
        value={code}
        onChange={(e) => {
          setCode(
            e.target.value
              .toUpperCase()
              .replace(/[^A-Z0-9]/g, '')
              .slice(0, 8),
          );
          setError(null);
        }}
        placeholder={t.classes.joinPlaceholder}
        aria-label={t.classes.joinTitle}
        aria-invalid={!!error}
        autoComplete="off"
        spellCheck={false}
        className="mono"
      />
      <button className="btn btn-primary" type="submit">
        {t.classes.join}
      </button>
      <FormError text={error} />
    </form>
  );
}

function ClassCard({ c }: { c: ClassSummary }) {
  const t = useT();
  return (
    <a href={`/classes/${c.id}`} onClick={onLinkClick} className="card class-card">
      <strong>{c.name}</strong>
      <span>
        {c.role === 'teacher'
          ? t.classes.students(c.studentCount)
          : t.classes.teacher(c.teacherName)}
      </span>
      {c.joinCode && (
        <span>
          {t.classes.joinCode}: <code className="mono">{c.joinCode}</code>
        </span>
      )}
    </a>
  );
}

/** `/classes` — classes I teach and classes I am in. */
export function ClassesPage() {
  const t = useT();
  const me = useMe();
  useTitle(t.classes.title);
  const [list, setList] = useState<ClassSummary[] | null>(null);
  const [name, setName] = useState('');
  const create = useAction();

  useEffect(() => {
    if (!me) return;
    let live = true;
    api
      .classes()
      .then((l) => live && setList(l))
      .catch(() => live && setList([]));
    return () => {
      live = false;
    };
  }, [me]);

  if (me === undefined) return <PageShell current="classes">{t.common.loading}</PageShell>;
  if (me === null) return <LoginRequired />;

  const teaching = list?.filter((c) => c.role === 'teacher') ?? [];
  const joined = list?.filter((c) => c.role === 'student') ?? [];
  const teacher = isTeacher(me);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void create.run(
      async () => {
        const c = await api.createClass(name.trim());
        navigate(`/classes/${c.id}`);
      },
      (err) =>
        err instanceof ApiError && err.status === 403
          ? t.classes.teachersOnly
          : t.common.somethingWrong,
    );
  };

  return (
    <PageShell current="classes">
      <div className="page-column page-column-wide">
        <h1 className="page-title">{t.classes.title}</h1>
        <AccountBanners />
        {teacher && (
          <section className="card form-card">
            <h2>{t.classes.newClass}</h2>
            <form className="inline-form" onSubmit={submit}>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.classes.namePlaceholder}
                aria-label={t.classes.newClass}
                maxLength={60}
                required
              />
              <button className="btn btn-primary" type="submit" disabled={create.busy}>
                <Icon name="plus" size={14} strokeWidth={2.6} />
                {t.classes.create}
              </button>
            </form>
            <FormError text={create.error} />
            {list && teaching.length === 0 && <p className="form-note">{t.classes.emptyTeacher}</p>}
            {teaching.length > 0 && (
              <div className="class-grid">
                {teaching.map((c) => (
                  <ClassCard key={c.id} c={c} />
                ))}
              </div>
            )}
          </section>
        )}
        <section className="card form-card">
          <h2>{t.classes.joinTitle}</h2>
          <p className="form-note">{t.classes.joinText}</p>
          <JoinClassForm />
          {list && joined.length === 0 && !teacher && (
            <p className="form-note">{t.classes.emptyStudent}</p>
          )}
          {joined.length > 0 && (
            <div className="class-grid">
              {joined.map((c) => (
                <ClassCard key={c.id} c={c} />
              ))}
            </div>
          )}
        </section>
      </div>
    </PageShell>
  );
}

function CopyField({ value, label }: { value: string; label: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <div className="link-box">
      <input readOnly value={value} aria-label={label} onFocus={(e) => e.target.select()} />
      <button
        className="btn"
        onClick={() => {
          void navigator.clipboard
            .writeText(value)
            .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
            .catch(() => undefined);
        }}
      >
        {copied ? t.common.copied : t.common.copy}
      </button>
    </div>
  );
}

/** `/classes/:id` — the teacher's class page (code, roster, homework) or a student's view. */
export function ClassPage({ id }: { id: string }) {
  const t = useT();
  const me = useMe();
  const [c, setC] = useState<ClassDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const act = useAction();
  useTitle(c?.name ?? null);

  useEffect(() => {
    if (!me) return;
    let live = true;
    api
      .classDetail(id)
      .then((d) => live && setC(d))
      .catch(() => live && setMissing(true));
    return () => {
      live = false;
    };
  }, [id, me]);

  if (me === undefined) return <PageShell current="classes">{t.common.loading}</PageShell>;
  if (me === null) return <LoginRequired />;
  if (missing) return <PageShell current="classes">{t.common.notFound}</PageShell>;
  if (!c) return <PageShell current="classes">{t.common.loading}</PageShell>;

  const teacher = c.role === 'teacher';
  const link = `${location.origin}/join/${c.joinCode ?? ''}`;
  const guarded = (key: string, fn: () => Promise<void>) => {
    if (confirm !== key) {
      setConfirm(key);
      return;
    }
    setConfirm(null);
    void act.run(fn, () => t.common.somethingWrong);
  };

  return (
    <PageShell current="classes">
      <div className="page-column page-column-wide">
        <a href="/classes" onClick={onLinkClick} className="back-link">
          <Icon name="chevronLeft" size={14} />
          {t.classes.back}
        </a>
        <div className="page-head">
          {renaming !== null ? (
            <form
              className="inline-form"
              onSubmit={(e) => {
                e.preventDefault();
                void act.run(
                  async () => {
                    setC(await api.renameClass(c.id, renaming.trim()));
                    setRenaming(null);
                  },
                  () => t.common.somethingWrong,
                );
              }}
            >
              <input
                value={renaming}
                onChange={(e) => setRenaming(e.target.value)}
                maxLength={60}
                aria-label={t.classes.rename}
                autoFocus
                required
              />
              <button className="btn btn-primary" type="submit">
                {t.account.save}
              </button>
            </form>
          ) : (
            <h1 className="page-title">{c.name}</h1>
          )}
          <span className="page-sub">
            {teacher ? t.classes.students(c.studentCount) : t.classes.teacher(c.teacherName)}
          </span>
          <div className="topbar-spacer" />
          {teacher && renaming === null && (
            <button className="btn btn-sm" onClick={() => setRenaming(c.name)}>
              <Icon name="pencil" size={13} />
              {t.classes.rename}
            </button>
          )}
          {teacher ? (
            <button
              className={`btn btn-sm${confirm === 'delete' ? ' btn-danger' : ''}`}
              onClick={() =>
                guarded('delete', async () => {
                  await api.deleteClass(c.id);
                  navigate('/classes');
                })
              }
            >
              {confirm === 'delete' ? t.classes.deleteConfirm(c.name) : t.classes.delete}
            </button>
          ) : (
            <button
              className={`btn btn-sm${confirm === 'leave' ? ' btn-danger' : ''}`}
              onClick={() =>
                guarded('leave', async () => {
                  await api.removeMember(c.id, me.id);
                  navigate('/classes');
                })
              }
            >
              {confirm === 'leave' ? t.classes.leaveConfirm(c.name) : t.classes.leave}
            </button>
          )}
        </div>
        <FormError text={act.error} />

        <div className={teacher ? 'two-col' : undefined}>
          <ClassHomework classDetail={c} />
          {teacher && (
            <aside className="page-column" style={{ gap: 16 }}>
              <section className="card form-card">
                <h2>{t.classes.joinCode}</h2>
                <div className="code-box">
                  <code>{c.joinCode}</code>
                  <button
                    className={`btn btn-sm${confirm === 'code' ? ' btn-danger' : ''}`}
                    onClick={() => guarded('code', async () => setC(await api.newClassCode(c.id)))}
                  >
                    {confirm === 'code' ? t.classes.newCodeConfirm : t.classes.newCode}
                  </button>
                </div>
                <span className="field-label">{t.classes.joinLink}</span>
                <CopyField value={link} label={t.classes.joinLink} />
              </section>
              <section className="card form-card">
                <h2>{t.classes.roster}</h2>
                {c.students.length === 0 ? (
                  <p className="form-note">{t.classes.noStudents}</p>
                ) : (
                  <ul className="roster">
                    {c.students.map((s) => (
                      <li key={s.id}>
                        <div className="who">
                          <strong>{s.displayName}</strong>
                          {s.email && <span>{s.email}</span>}
                        </div>
                        <button
                          className={`btn btn-sm${confirm === `rm-${s.id}` ? ' btn-danger' : ''}`}
                          onClick={() =>
                            guarded(`rm-${s.id}`, async () => {
                              await api.removeMember(c.id, s.id);
                              setC(await api.classDetail(c.id));
                            })
                          }
                        >
                          {confirm === `rm-${s.id}`
                            ? t.classes.removeConfirm(s.displayName)
                            : t.classes.remove}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </aside>
          )}
        </div>
      </div>
    </PageShell>
  );
}

/** `/join/:code` — the link a teacher shares. */
export function JoinPage({ code }: { code: string }) {
  const t = useT();
  const me = useMe();
  const [preview, setPreview] = useState<JoinPreview | null>(null);
  const [missing, setMissing] = useState(false);
  const act = useAction();
  useTitle(t.classes.joinTitle);

  useEffect(() => {
    if (me === undefined) return;
    let live = true;
    api
      .joinPreview(code)
      .then((p) => live && setPreview(p))
      .catch(() => live && setMissing(true));
    return () => {
      live = false;
    };
  }, [code, me]);

  if (missing) {
    return (
      <PageShell>
        <div className="card auth-card">
          <p className="auth-lead">{t.classes.joinNotFound}</p>
          <JoinClassForm />
        </div>
      </PageShell>
    );
  }
  if (!preview || me === undefined) return <PageShell>{t.common.loading}</PageShell>;
  if (me === null) return <LoginRequired text={t.classes.joinNeedsLogin(preview.className)} />;

  return (
    <PageShell>
      <div className="card auth-card">
        <h1>{preview.className}</h1>
        <p className="auth-lead">{t.classes.teacher(preview.teacherName)}</p>
        {preview.alreadyMember ? (
          <>
            <p>{t.classes.alreadyMember(preview.className)}</p>
            <a
              href={`/classes/${preview.classId}`}
              onClick={onLinkClick}
              className="btn btn-primary btn-lg"
            >
              {t.classes.open}
            </a>
          </>
        ) : !me.active ? (
          <>
            <p className="form-note">{t.classes.notActive}</p>
            <AccountBanners />
          </>
        ) : (
          <>
            <p>{t.classes.joinConfirm(preview.className, preview.teacherName)}</p>
            <FormError text={act.error} />
            <button
              className="btn btn-primary btn-lg"
              disabled={act.busy}
              onClick={() =>
                void act.run(
                  async () => {
                    const c = await api.joinClass(code);
                    navigate(`/classes/${c.id}`);
                  },
                  () => t.common.somethingWrong,
                )
              }
            >
              {t.classes.join}
            </button>
          </>
        )}
      </div>
    </PageShell>
  );
}
